import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../shared/lib/supabase'

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 }

export function pickActiveTerm(terms) {
  if (!Array.isArray(terms) || terms.length === 0) return null
  const active = terms
    .filter(t => t.status === 'active')
    .sort((a, b) => (b.effective_date || '').localeCompare(a.effective_date || ''))
  if (active.length > 0) return active[0]
  const fallback = [...terms].sort((a, b) => (b.effective_date || '').localeCompare(a.effective_date || ''))
  return fallback[0] || null
}

export function pickLatestV2Review(reviews) {
  if (!Array.isArray(reviews) || reviews.length === 0) return null
  const v2 = reviews
    .filter(r => r.data_format_version === 'v2')
    .sort((a, b) => (b.reviewed_at || '').localeCompare(a.reviewed_at || ''))
  return v2[0] || null
}

export function sortFindings(findings) {
  if (!Array.isArray(findings)) return []
  return [...findings].sort((a, b) => {
    const sa = SEVERITY_ORDER[a.severity] ?? SEVERITY_ORDER.low
    const sb = SEVERITY_ORDER[b.severity] ?? SEVERITY_ORDER.low
    if (sa !== sb) return sa - sb
    return (b.adjusted_confidence ?? 0) - (a.adjusted_confidence ?? 0)
  })
}

export default function useInsuranceReview(clientId) {
  const [state, setState] = useState({
    loading: true, error: null,
    term: null, review: null, findings: [], sourceDocuments: []
  })

  const load = useCallback(async () => {
    if (!clientId) { setState(s => ({ ...s, loading: false })); return }
    setState(s => ({ ...s, loading: true, error: null }))
    try {
      const { data: terms, error: termsErr } = await supabase
        .from('client_insurance_policy_terms')
        .select('id, carrier, policy_label, effective_date, expiration_date, status, layer_type')
        .eq('client_id', clientId)
      if (termsErr) throw termsErr

      const term = pickActiveTerm(terms)
      if (!term) {
        setState({ loading: false, error: null, term: null, review: null, findings: [], sourceDocuments: [] })
        return
      }

      const { data: reviews, error: reviewsErr } = await supabase
        .from('client_insurance_policy_reviews')
        .select('*')
        .eq('client_id', clientId)
        .eq('policy_term_id', term.id)
      if (reviewsErr) throw reviewsErr

      const review = pickLatestV2Review(reviews)
      if (!review) {
        setState({ loading: false, error: null, term, review: null, findings: [], sourceDocuments: [] })
        return
      }

      const { data: rawFindings, error: findingsErr } = await supabase
        .from('client_insurance_review_findings')
        .select('*')
        .eq('review_id', review.id)
      if (findingsErr) throw findingsErr

      const docIds = [review.document_id, ...(review.document_ids || [])].filter(Boolean)
      let sourceDocuments = []
      if (docIds.length > 0) {
        const { data: docs, error: docsErr } = await supabase
          .from('client_documents')
          .select('id, file_name, file_path, description, created_at')
          .in('id', docIds)
        if (docsErr) throw docsErr
        sourceDocuments = docs || []
      }

      setState({
        loading: false, error: null,
        term, review,
        findings: sortFindings(rawFindings || []),
        sourceDocuments
      })
    } catch (err) {
      setState({ loading: false, error: err.message || String(err), term: null, review: null, findings: [], sourceDocuments: [] })
    }
  }, [clientId])

  useEffect(() => { load() }, [load])

  return { ...state, refetch: load }
}
